import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, QrCodeIcon, SendIcon } from 'lucide-react';
import { CardLogos } from '../components/payment/CardLogos';
import { PaymentForm } from '../components/payment/PaymentForm';
import { OTPForm } from '../components/payment/OTPForm';
import { ProcessingAnimation } from '../components/payment/ProcessingAnimation';
import { RaastQRPayment } from '../components/payment/RaastQRPayment';
import { RaastRTPPayment } from '../components/payment/RaastRTPPayment';
import { PayVayWalletPayment } from '../components/payment/PayVayWalletPayment';
import { Modal } from '../components/ui/Modal';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../utils/formatters';
import { useScreenInit } from '../useScreenInit.js';
type PaymentStep = 'details' | 'otp' | 'processing';
export function CheckoutPage() {
  const navigate = useNavigate();
  const {
    subtotal,
    shippingCost,
    total,
    itemCount,
    clearCart
  } = useCart();
  const screenInit = useScreenInit() as {
    paymentMethod?: string;
    raastSubMethod?: 'qr' | 'rtp';
    modalOpen?: boolean;
    paymentStep?: PaymentStep;
    rtpStep?: 'input' | 'confirm' | 'waiting';
    payvayStep?: 'input' | 'waiting';
  };
  const [paymentMethod, setPaymentMethod] = useState(screenInit.paymentMethod ?? 'card');
  const [raastSubMethod, setRaastSubMethod] = useState<'qr' | 'rtp'>(screenInit.raastSubMethod ?? 'qr');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Payment Modal State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(!!screenInit.modalOpen);
  const [paymentStep, setPaymentStep] = useState<PaymentStep>(screenInit.paymentStep ?? 'details');
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentData, setPaymentData] = useState<any>(null);
  const [savedTotal, setSavedTotal] = useState(0);
  // Redirect to cart if empty and not currently processing an order
  useEffect(() => {
    if (itemCount === 0 && !isPaymentModalOpen && !isSubmitting) {
      navigate('/cart');
    }
  }, [itemCount, navigate, isPaymentModalOpen, isSubmitting]);
  // Save total when modal opens so it persists through cart clear
  useEffect(() => {
    if (isPaymentModalOpen && total > 0) {
      setSavedTotal(total);
    }
  }, [isPaymentModalOpen, total]);
  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentMethod === 'card' || paymentMethod === 'raast' || paymentMethod === 'payvay') {
      // Open payment modal for card, RAAST or PayVay payments
      setPaymentStep('details');
      setIsPaymentModalOpen(true);
    } else {
      // Handle COD directly
      setIsSubmitting(true);
      setTimeout(() => {
        const orderId = `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
        clearCart();
        navigate('/order-confirmation', {
          state: {
            orderId,
            amount: total,
            paymentMethod: 'Cash on Delivery',
            paymentStatus: 'Pending'
          }
        });
      }, 1000);
    }
  };
  // --- Payment Modal Handlers ---
  const handlePaymentSubmit = (data: any) => {
    setPaymentData(data);
    setPaymentStep('otp');
  };
  const handleOTPSubmit = (otp: string) => {
    setPaymentStep('processing');
    setPaymentStatus('processing');
    // Simulate API call
    setTimeout(() => {
      setPaymentStatus('success');
    }, 4000);
  };
  const handlePaymentCancel = () => {
    setIsPaymentModalOpen(false);
    setPaymentStep('details');
    setIsSubmitting(false);
  };
  const handlePaymentComplete = () => {
    setIsSubmitting(true);
    setIsPaymentModalOpen(false);
    const orderId = `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
    const finalAmount = savedTotal || total;
    const finalCardLast4 = paymentMethod === 'card' ? paymentData?.cardNumber?.slice(-4) || '****' : undefined;
    const finalPaymentMethod = paymentMethod === 'raast' ? raastSubMethod === 'qr' ? 'RAAST QR' : 'RAAST Request to Pay' : paymentMethod === 'payvay' ? 'PayVay Wallet' : 'Credit/Debit Card';
    clearCart();
    navigate('/order-confirmation', {
      state: {
        orderId,
        amount: finalAmount,
        paymentMethod: finalPaymentMethod,
        paymentStatus: 'Paid',
        receiptNo: `REC-${Math.floor(10000000 + Math.random() * 90000000)}`,
        date: new Date().toISOString(),
        cardLast4: finalCardLast4
      }
    });
  };
  return <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8" data-id="element-1158" data-mp="y3i5fyrrrbva">
      <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1159" data-mp="12xw88m8rgl9z">Checkout</h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8" data-id="element-1160" data-mp="17vxn0uz5lyfn">
        <div className="md:col-span-8" data-id="element-1161" data-mp="17xilgvola1is">
          <form onSubmit={handlePlaceOrder} className="space-y-8" data-id="element-1162" data-mp="1cvseazmucq18">
            {/* Shipping Info */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-1163" data-mp="1gxdzd89j4ami">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1164" data-mp="31en4qommj02s">
                Shipping Information
              </h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-1165" data-mp="2xbqfif2kw5pv">
                <div data-id="element-1166" data-mp="2sdp0q6c6qsk7">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1167" data-mp="2sd6x2on71c29">
                    First Name
                  </label>
                  <input type="text" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" defaultValue="John" required data-id="element-1168" data-mp="2ng6r0dqcg9b5" />
                </div>
                <div data-id="element-1169" data-mp="3g4lzeheyztdi">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1170" data-mp="3g5dxpzzyc57h">
                    Last Name
                  </label>
                  <input type="text" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" defaultValue="Doe" required data-id="element-1171" data-mp="36dzrl9dhw75d" />
                </div>
                <div className="col-span-2" data-id="element-1172" data-mp="2wr8km717192">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1173" data-mp="2vzg3k8qdjn0">
                    Address
                  </label>
                  <input type="text" className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" defaultValue="123 Main St" required data-id="element-1174" data-mp="1x6z77xqki5fa" />
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-1175" data-mp="1sc20ixgrg85k">
              <h2 className="text-xl font-semibold mb-4" data-id="element-1176" data-mp="1dfudzqhvqun4">Payment Method</h2>

              <div className="space-y-4" data-id="element-1177" data-mp="1ifyxzqhzy7i3">
                {/* Credit/Debit Card Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1178" data-mp="14gpjqwdcpngr">
                  <div className="flex items-start" data-id="element-1179" data-mp="2ytp6d9g5fhxj">
                    <div className="flex items-center h-5" data-id="element-1180" data-mp="2ys4a9fe6rqxk">
                      <input type="radio" name="paymentMethod" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1181" data-mp="33rqp1t9nxf8h" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-1182" data-mp="3mpadohmuel0l">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1183" data-mp="3mq3lzn1g9iku">
                        Credit/Debit Card
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1184" data-mp="i3sfuz9ig8k">
                        Pay securely with your card via Paysys Labs gateway.
                      </span>
                      <CardLogos data-id="element-1185" data-mp="3rqrbu5dxztdh" />
                    </div>
                  </div>
                </label>

                {/* RAAST Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'raast' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1186" data-mp="5gx5xneju78q">
                  <div className="flex items-start" data-id="element-1187" data-mp="279lca5mhpasq">
                    <div className="flex items-center h-5" data-id="element-1188" data-mp="27awfa3842eis">
                      <input type="radio" name="paymentMethod" value="raast" checked={paymentMethod === 'raast'} onChange={() => setPaymentMethod('raast')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1189" data-mp="2vsot8jnbb6ja" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-1190" data-mp="39kn0eoxgqsct">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1191" data-mp="39kd8uwwrsd50">
                        RAAST
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1192" data-mp="2htpafsmhdlv1">
                        Pay instantly via RAAST QR or Request to Pay.
                      </span>
                      <div className="mt-3" data-id="element-1193" data-mp="2lxeuc4s7nf9q">
                        <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-6 object-contain" data-id="element-1194" data-mp="2quwvh8zzkq76" />
                      </div>

                      {/* RAAST Sub-options */}
                      {paymentMethod === 'raast' && <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-2 gap-3" data-id="element-1195" data-mp="65zcm7wsl45a">
                          <button type="button" onClick={e => {
                        e.preventDefault();
                        setRaastSubMethod('qr');
                      }} className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${raastSubMethod === 'qr' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1196" data-mp="g9xmve1pyuoi">
                            {raastSubMethod === 'qr' && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-1197" data-mp="3onmz39u8p54r" />}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${raastSubMethod === 'qr' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-1198" data-mp="3jpuxfo3sm4nd">
                              <QrCodeIcon className="w-5 h-5" data-id="element-1199" data-mp="1hxq7uj9rnjft" />
                            </div>
                            <div className="text-center" data-id="element-1200" data-mp="1dvljo0237f0k">
                              <p className={`text-xs font-semibold ${raastSubMethod === 'qr' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-1201" data-mp="1du0qadrie24a">
                                RAAST QR
                              </p>
                              <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-1202" data-mp="1huwi76gcostv">
                                Scan & Pay
                              </p>
                            </div>
                          </button>
                          <button type="button" onClick={e => {
                        e.preventDefault();
                        setRaastSubMethod('rtp');
                      }} className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${raastSubMethod === 'rtp' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1203" data-mp="p5h9inc5rc14">
                            {raastSubMethod === 'rtp' && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-1204" data-mp="6dwwitoidc8t" />}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${raastSubMethod === 'rtp' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-1205" data-mp="1ek7q3e2tv3l">
                              <SendIcon className="w-5 h-5" data-id="element-1206" data-mp="2hwtac8gdrugu" />
                            </div>
                            <div className="text-center" data-id="element-1207" data-mp="2456ci46sidvy">
                              <p className={`text-xs font-semibold ${raastSubMethod === 'rtp' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-1208" data-mp="2845htpwfocnx">
                                Request to Pay
                              </p>
                              <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-1209" data-mp="30sb1safnxxmp">
                                IBAN / Mobile
                              </p>
                            </div>
                          </button>
                        </div>}
                    </div>
                  </div>
                </label>

                {/* PayVay Wallet Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'payvay' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1210" data-mp="1geaeuqfnkogs">
                  <div className="flex items-start" data-id="element-1211" data-mp="3d3k3b9xzzkxs">
                    <div className="flex items-center h-5" data-id="element-1212" data-mp="3d5wt299zyzle">
                      <input type="radio" name="paymentMethod" value="payvay" checked={paymentMethod === 'payvay'} onChange={() => setPaymentMethod('payvay')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1213" data-mp="38bso1vkal1my" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-1214" data-mp="2udc77bsptur3">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1215" data-mp="2ucki8jtmiv9s">
                        PayVay Wallet
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1216" data-mp="2lcnuckf260fs">
                        Pay with your PayVay Wallet mobile number.
                      </span>
                      <div className="mt-3" data-id="element-1217" data-mp="nm37ne3xx7s5">
                        <img src="https://cdn.magicpatterns.com/uploads/vLecFTCvQiPVbcveGEWCvz/PayVay1.png" alt="PayVay" className="h-6 object-contain" data-id="element-1218" data-mp="jkrjld0psum2" />
                      </div>
                    </div>
                  </div>
                </label>

                {/* Cash on Delivery Option */}
                <label className={`block relative border rounded-lg p-4 cursor-pointer transition-colors ${paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1219" data-mp="2b87fnudbepdk">
                  <div className="flex items-center" data-id="element-1220" data-mp="1ikto6bsdym0u">
                    <div className="flex items-center h-5" data-id="element-1221" data-mp="1im4og1mmhbn7">
                      <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1222" data-mp="1njxw6z3kqdpl" />
                    </div>
                    <div className="ml-3" data-id="element-1223" data-mp="1whgo8nfmxsaz">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-1224" data-mp="32yvdoxjozen0">
                        Cash on Delivery
                      </span>
                      <span className="block text-sm text-gray-500 mt-1" data-id="element-1225" data-mp="380t3ybqif9pg">
                        Pay when you receive your order.
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-end" data-id="element-1226" data-mp="3fk2azybjmwbc">
              <button type="submit" disabled={isSubmitting || itemCount === 0} className="bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-1227" data-mp="3bh5nbt75ca0j">
                {isSubmitting ? 'Processing...' : paymentMethod === 'card' || paymentMethod === 'raast' || paymentMethod === 'payvay' ? 'Proceed to Payment' : 'Place Order'}
              </button>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="md:col-span-4" data-id="element-1228" data-mp="c2bq9yznejzq">
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 sticky top-6" data-id="element-1229" data-mp="72phq2urlrx9">
            <h2 className="text-lg font-semibold mb-4" data-id="element-1230" data-mp="70uv8k3uhrr2">Order Summary</h2>
            <div className="space-y-3 text-sm" data-id="element-1231" data-mp="qpag5piux6cc">
              <div className="flex justify-between" data-id="element-1232" data-mp="vo3wrqtfxdgq">
                <span className="text-gray-600" data-id="element-1233" data-mp="vmspvi4p7ple">
                  Subtotal ({itemCount} items)
                </span>
                <span className="font-medium" data-id="element-1234" data-mp="hq5moxm5k371">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between" data-id="element-1235" data-mp="lr90lekn3p5m">
                <span className="text-gray-600" data-id="element-1236" data-mp="7wyel5012939">Shipping</span>
                <span className="font-medium" data-id="element-1237" data-mp="cog7d5rwd3d5">
                  {shippingCost === 0 ? <span className="text-green-600" data-id="element-1238" data-mp="cqszgbrdcu3x">Free</span> : formatPrice(shippingCost)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg" data-id="element-1239" data-mp="2yn5iwhk42m3">
                <span data-id="element-1240" data-mp="3l3zvjqdpnvlc">Total</span>
                <span data-id="element-1241" data-mp="3l5ug33l2llow">{formatPrice(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal Overlay */}
      <Modal isOpen={isPaymentModalOpen} onClose={handlePaymentCancel} size="lg" data-id="element-1242" data-mp="1vn4ei50frxyd">
        <div className="-m-4 flex flex-col font-sans overflow-hidden rounded-2xl" data-id="element-1243" data-mp="1qwnz4fd2229d">
          {/* Paysys Labs Header */}
          <header className="bg-white border-b border-gray-200 py-4 px-6 shadow-sm" data-id="element-1244" data-mp="1lx2y7zebrju0">
            <div className="flex justify-between items-center" data-id="element-1245" data-mp="1hw9bh9cmcb2p">
              <div className="flex items-center space-x-2" data-id="element-1246" data-mp="1hxu5mxv0ts01">
                <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-7 object-contain" data-id="element-1247" data-mp="2emi7ajmfj2na" />
              </div>
              <div className="flex items-center text-sm text-gray-500" data-id="element-1248" data-mp="25lbq08byhnsz">
                <Shield className="w-4 h-4 mr-1 text-green-500" data-id="element-1249" data-mp="25nogxazoz8i6" />
                <span className="hidden sm:inline" data-id="element-1250" data-mp="20lpeq4fouhmt">Secure Checkout</span>
              </div>
            </div>
          </header>

          {/* Progress Bar */}
          <div className="h-1 w-full bg-gray-100" data-id="element-1251" data-mp="p7lj6ttgclfn">
            <div className="h-full bg-blue-600 transition-all duration-500 ease-in-out" style={{
            width: paymentMethod === 'raast' || paymentMethod === 'payvay' ? '100%' : paymentStep === 'details' ? '33%' : paymentStep === 'otp' ? '66%' : '100%'
          }} data-id="element-1252" data-mp="p5r0zn9kf7f9" />
          </div>

          {/* Main Content Area */}
          <div className="p-6 sm:p-8 bg-white min-h-[400px] flex flex-col justify-center" data-id="element-1253" data-mp="39tiw3pbrvuhu">
            {paymentMethod === 'payvay' ? <PayVayWalletPayment initialStep={screenInit.payvayStep} totalAmount={total} onComplete={handlePaymentComplete} onCancel={handlePaymentCancel} data-id="element-1254" data-mp="3jlf4pubhj30b" /> : paymentMethod === 'raast' ? raastSubMethod === 'qr' ? <RaastQRPayment totalAmount={total} onComplete={handlePaymentComplete} onCancel={handlePaymentCancel} data-id="element-1255" data-mp="3tjmcbmjvw8pn" /> : <RaastRTPPayment initialStep={screenInit.rtpStep} totalAmount={total} onComplete={handlePaymentComplete} onCancel={handlePaymentCancel} data-id="element-1256" data-mp="1g24k7lm9tnc" /> : <>
                {paymentStep === 'details' && <>
                    <div className="mb-6" data-id="element-1257" data-mp="2365b06ctkrc8">
                      <h2 className="text-2xl font-bold text-gray-900" data-id="element-1258" data-mp="238zspq4g7y1y">
                        Payment Details
                      </h2>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1259" data-mp="1fc3i0j881p8b">
                        Enter your card information to complete the purchase.
                      </p>
                    </div>
                    <PaymentForm onSubmit={handlePaymentSubmit} onCancel={handlePaymentCancel} totalAmount={total} data-id="element-1260" data-mp="1p1myxjcdj90j" />
                  </>}

                {paymentStep === 'otp' && <OTPForm onSubmit={handleOTPSubmit} onCancel={handlePaymentCancel} amount={savedTotal || total} cardLast4={paymentData?.cardNumber?.replace(/\s/g, '').slice(-4) || '****'} cardScheme={(() => {
              const num = paymentData?.cardNumber?.replace(/\D/g, '') || '';
              if (num.startsWith('2')) return 'PayPak';
              if (num.startsWith('4')) return 'Visa';
              if (num.startsWith('5')) return 'Mastercard';
              if (num.startsWith('6')) return 'UnionPay';
              return 'Visa';
            })()} data-id="element-1261" data-mp="11dwsprhc8xk9" />}

                {paymentStep === 'processing' && <ProcessingAnimation status={paymentStatus} onComplete={handlePaymentComplete} data-id="element-1262" data-mp="2pbo4ia1y3wcx" />}
              </>}
          </div>

          {/* Footer inside modal */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-1263" data-mp="2fcfaxiazsjr3">
            <div className="flex items-center text-xs text-gray-500" data-id="element-1264" data-mp="2kckzuqpvns5h">
              <Lock className="w-3 h-3 mr-1" data-id="element-1265" data-mp="381e0h0hg2o6f" />
              256-bit SSL Encryption
            </div>
            <div className="flex items-center space-x-2" data-id="element-1266" data-mp="3cymhgxquwm63">
              <img src="https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png" alt="Visa" className="h-4 opacity-60" data-id="element-1267" data-mp="2yarnzf50ydry" />
              <img src="https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png" alt="Mastercard" className="h-4 opacity-60" data-id="element-1268" data-mp="337ru1q1vjgj2" />
              <img src="https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png" alt="UnionPay" className="h-4 opacity-60" data-id="element-1269" data-mp="3vpdo3rjglygs" />
              <img src="https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png" alt="PayPak" className="h-4 opacity-60" data-id="element-1270" data-mp="1r5vqlaedwyxp" />
              <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-4 opacity-60" data-id="element-1271" data-mp="1hfkc4tx2r7ck" />
            </div>
          </div>
        </div>
      </Modal>
    </div>;
}